import {
  EActionsMenuRowState,
  TActionsMenuBlocked,
  TActionsMenuConfirm,
  TActionsMenuLayout,
  TActionsMenuLayoutInput,
  TActionsMenuRecord,
  TActionsMenuRowView,
  TActionsMenuRun,
} from './grid-actions-menu.model';

type TActionsMenuRowStyle = Pick<TActionsMenuRowView, 'rowClass' | 'buttonClass' | 'tileClass' | 'icon' | 'statusClass'>;

const ACTIONS_MENU_ROW_STYLES: Record<EActionsMenuRowState, TActionsMenuRowStyle> = {
  [EActionsMenuRowState.Idle]: {
    rowClass: '',
    buttonClass: 'hover:bg-accent-transparent',
    tileClass: '',
    icon: '',
    statusClass: 'text-muted-foreground',
  },
  [EActionsMenuRowState.Waiting]: {
    rowClass: 'bg-accent-transparent',
    buttonClass: 'cursor-progress',
    tileClass: '',
    icon: '',
    statusClass: 'text-muted-foreground',
  },
  [EActionsMenuRowState.Loading]: {
    rowClass: 'bg-primary-10 dark:bg-primary-20',
    buttonClass: 'cursor-progress',
    tileClass: 'bg-background',
    icon: '',
    statusClass: 'text-foreground-75',
  },
  [EActionsMenuRowState.Confirm]: {
    rowClass: 'bg-warning-fade',
    buttonClass: 'cursor-default',
    tileClass: 'bg-background text-warning-foreground',
    icon: 'info',
    statusClass: 'text-foreground',
  },
  [EActionsMenuRowState.Done]: {
    rowClass: 'bg-success-fade',
    buttonClass: '',
    tileClass: 'bg-background text-success-foreground',
    icon: 'check',
    statusClass: 'text-foreground',
  },
  [EActionsMenuRowState.Empty]: {
    rowClass: 'bg-warning-fade',
    buttonClass: '',
    tileClass: 'bg-background text-warning-foreground',
    icon: 'info',
    statusClass: 'text-foreground',
  },
  [EActionsMenuRowState.SelectionRequired]: {
    rowClass: 'bg-warning-fade',
    buttonClass: '',
    tileClass: 'bg-background text-warning-foreground',
    icon: 'info',
    statusClass: 'text-foreground',
  },
  [EActionsMenuRowState.Error]: {
    rowClass: 'bg-danger-fade',
    buttonClass: '',
    tileClass: 'bg-background text-danger-foreground',
    icon: 'exclamation',
    statusClass: 'text-foreground',
  },
  [EActionsMenuRowState.Blocked]: {
    rowClass: '',
    buttonClass: 'hover:bg-accent-transparent',
    tileClass: '',
    icon: '',
    statusClass: 'text-foreground-75',
  },
};

const buildActionsMenuRow = (state: EActionsMenuRowState, overrides: Partial<TActionsMenuRowView> = {}): TActionsMenuRowView => ({
  state,
  ...ACTIONS_MENU_ROW_STYLES[state],
  spinner: state === EActionsMenuRowState.Loading,
  statusKey: '',
  secondLineKey: '',
  count: 0,
  total: 0,
  limit: 0,
  action: '',
  busy: state === EActionsMenuRowState.Waiting || state === EActionsMenuRowState.Loading,
  showBadge: state !== EActionsMenuRowState.Error && state !== EActionsMenuRowState.Empty && state !== EActionsMenuRowState.SelectionRequired,
  showRetry: state === EActionsMenuRowState.Error,
  showConfirm: state === EActionsMenuRowState.Confirm,
  ...overrides,
});

export const ACTIONS_MENU_IDLE_ROW: TActionsMenuRowView = buildActionsMenuRow(EActionsMenuRowState.Idle);

export const buildActionsMenuRows = (
  run: TActionsMenuRun | null,
  records: Record<string, TActionsMenuRecord>,
  confirm: TActionsMenuConfirm | null,
  blocked: TActionsMenuBlocked | null,
  selectedCount = 0,
): Record<string, TActionsMenuRowView> => {
  const rows: Record<string, TActionsMenuRowView> = {};

  Object.entries(records)
    .filter(([, record]) => record.state !== EActionsMenuRowState.SelectionRequired || selectedCount === 0)
    .forEach(([id, record]) => {
      rows[id] = buildActionsMenuRow(record.state, {
        statusKey: record.messageKey,
        count: record.count,
        secondLineKey:
          record.hintKey ||
          (record.state === EActionsMenuRowState.Error && record.failures > 1 ? 'COMPONENTS.DX.ACTIONS_MENU.ERROR_PERSISTS' : ''),
      });
    });

  if (blocked) {
    rows[blocked.id] = buildActionsMenuRow(EActionsMenuRowState.Blocked, {
      statusKey: 'COMPONENTS.DX.ACTIONS_MENU.WAIT_RUNNING',
      action: blocked.runningId,
    });
  }

  if (confirm) {
    rows[confirm.id] = buildActionsMenuRow(EActionsMenuRowState.Confirm, {
      statusKey: 'COMPONENTS.DX.ACTIONS_MENU.LIMIT_CONFIRM',
      secondLineKey: 'COMPONENTS.DX.ACTIONS_MENU.LIMIT_HINT',
      total: confirm.total,
      limit: confirm.limit,
    });
  }

  if (run) {
    rows[run.id] = run.loading
      ? buildActionsMenuRow(EActionsMenuRowState.Loading, {
          statusKey: run.loadingKey,
          secondLineKey: run.longWait ? 'COMPONENTS.DX.ACTIONS_MENU.LONG_WAIT' : '',
        })
      : buildActionsMenuRow(EActionsMenuRowState.Waiting);
  }

  return rows;
};

export const getDownloadedMessageKey = (count: number): string =>
  count === 1 ? 'COMPONENTS.DX.ACTIONS_MENU.DOWNLOADED_ONE' : 'COMPONENTS.DX.ACTIONS_MENU.DOWNLOADED';

export const markActionsMenuRecordsSeen = (records: Record<string, TActionsMenuRecord>): Record<string, TActionsMenuRecord> =>
  Object.fromEntries(Object.entries(records).map(([id, record]) => [id, { ...record, seen: true }]));

export const dropSeenActionsMenuRecords = (records: Record<string, TActionsMenuRecord>): Record<string, TActionsMenuRecord> =>
  Object.fromEntries(Object.entries(records).filter(([, record]) => !record.seen));

export const findUnseenActionsMenuError = (records: Record<string, TActionsMenuRecord>): string | undefined =>
  Object.keys(records).find((id) => records[id].state === EActionsMenuRowState.Error && !records[id].seen);

export const omitActionsMenuRecord = (records: Record<string, TActionsMenuRecord>, id: string): Record<string, TActionsMenuRecord> =>
  Object.fromEntries(Object.entries(records).filter(([recordId]) => recordId !== id));

export const buildActionsMenuLayout = (input: TActionsMenuLayoutInput): TActionsMenuLayout => {
  const exportGroup = input.allowExport;
  const selectionGroup = input.selectionCount > 0;
  const generalGroup = input.generalCount > 0 || input.hasRememberFilter;
  const refresh = input.showRefreshButton || input.allowExport;
  const groupBeforeRefresh = exportGroup || selectionGroup || generalGroup;

  return {
    exportGroup,
    selectionGroup,
    generalGroup,
    refresh,
    selectionDivider: exportGroup,
    generalDivider: exportGroup || selectionGroup,
    refreshDivider: groupBeforeRefresh,
    refreshHeading: refresh && !groupBeforeRefresh,
    hasActions: groupBeforeRefresh || refresh,
  };
};
